import React from "react";
import { Secondary } from "../../ui-components/CustomButton";
import { CircularProgress, Grid, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import linkedInIcon from '../../assets/linkedin_icon.png'
import Cookies from "js-cookie"
import { jwtDecode } from 'jwt-decode'
import { LoginSocialLinkedin } from 'reactjs-social-login';
import { LoginViaEmail } from "api";
import { checkForDomain, getDeviceType, setTokenForAuthorization, captureUtmParams } from "utilities/index";
import { hasPendingSubscriptionIntent } from "utilities/subscriptionIntent";
import { ExchangeForToken } from "api/authentication";
import { useToast } from "context/ToastContext";

const LINKEDIN_CLIENT_ID = process.env.REACT_APP_LINKEDIN_APP_ID;
const LINKEDIN_CLIENT_SECRET = process.env.REACT_APP_LINKEDIN_APP_SECRET;
const REDIRECT_URI = `${window.location.origin}/callback`;
const STATE = Math.random().toString(36).substring(2); // Secure random string

const LinkedinLogin = () => {
  // const providerCookies = Cookies.get("provider")
  const currentIP = window.location.host;
  const currentLOcation = window.location.pathname;
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const loginMethod = "linkedin";
  const { mutateAsync: exchangeForTokenAsync, status: exchangeForTokenStatus, error: exchangeForTokenError, isLoading: isLoadingExchangeForToken, data: exchangeForTokenResponse } = ExchangeForToken();
  const { mutateAsync: loginViaEmailAsync, status: LoginViaEmailStatus, error: LoginViaEmailError, isLoading, data: loginViaEmailResponse } = LoginViaEmail();

  const onLoginStart = () => {
    // console.log('Login started');
  };

  const onResolve = async ({ provider, data }) => {
    const decodedData = jwtDecode(data?.id_token)
    // console.log('Login successful',decodedData);
    Cookies.set("userDetails", JSON.stringify({ first_name: decodedData?.given_name, last_name: decodedData?.family_name }))
    await loginViaEmailAsync({
      "email": decodedData?.email,
      ...(searchParams.get("ref") ? { "ref": searchParams.get("ref") } : {}),
      ...(searchParams.get("utm_source") ? { utm_source: searchParams.get("utm_source") } : {}),
      ...(searchParams.get("utm_campaign") ? { utm_campaign: searchParams.get("utm_campaign") } : {}),
      "devices": {
        "device_token": `${Math.random() * 10}`,
        "device_type": getDeviceType()
      },
      "last_login_ip": `${currentIP}`,
      "auth_provider": "linkedin"
    }, {
      onSuccess: (verifyResponse) => {
        captureUtmParams()
        if (!searchParams.get("ref")) {
          Cookies.set("pathname", window.location?.pathname + window.location?.search);
        }
        else {
          Cookies.set("pathname", "/");
        }
        const response = verifyResponse?.data?.data;
        if (response?.userData?.investor_name) {
          addToast(`You are added to ${response?.userData?.investor_name}'s pipeline.`, "success")
        }
        if (currentIP?.includes("localhost")) {
          if (response?.collaborator_data?.connected_id) {
            Cookies.set("X-ST-ID", response?.collaborator_data?.connected_id);
          }
          response?.userData?.token && setTokenForAuthorization(response?.userData?.token);
        }
        // Session to be same for current users:
        if (response?.collaborator_data?.connected_id) {
          Cookies.set("X-ST-ID", response?.collaborator_data?.connected_id, { domain: checkForDomain() });
        }
        response?.userData?.token && setTokenForAuthorization(response?.userData?.token, checkForDomain());
        Cookies.set("email", decodedData?.email);
        if (currentLOcation?.includes("register") && searchParams.get("ref")) {
          Cookies.set("ref", searchParams.get("ref"))
        }
        Cookies.set("provider", "linkedin")
        if (response?.is_profile_completed == true) {
          Cookies.set("is_profile_complete", true, { domain: checkForDomain() })
          if (hasPendingSubscriptionIntent()) {
            navigate("/?upgrade=true");
          }
          else if (Cookies.get("pathname")) {
            navigate(Cookies.get("pathname"));
            Cookies.remove("pathname")
          }
          else {
            navigate(currentLOcation !== "/login" && currentLOcation !== "/verification" && currentLOcation !== "/register" ? currentLOcation : "/");
          }
        }
        else {
          Cookies.set("is_profile_complete", false, { domain: checkForDomain() })
          navigate("/");
          Cookies.set("step", Number(response?.step))
        }
      },
      onError: () => {
        // setEmailError(true);
      }
    });

  };

  const onReject = (err) => {
    console.error('Login failed', err);
  };

  React.useEffect(() => {
    const handleMessage = async (event) => {
      // console.log(event, "event")
      if (event.origin !== window.location.origin) return;
      if (event.data.provider === "linkedin") {
        onResolve({
          provider: loginMethod, data: event.data?.details
        })
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem('linkedin_oauth_state');
    // console.log(code,"code", state, expectedState)
    if (code ?? (code && window.location.pathname === "/callback")) {
      exchangeForTokenAsync({ code, redirect_uri: REDIRECT_URI, client_id: LINKEDIN_CLIENT_ID, client_secret: LINKEDIN_CLIENT_SECRET }).then((response) => {
        // console.log(response)
        if (window.opener) {
          window.opener.postMessage({
            provider: 'linkedin',
            details: response.data.details,
          }, window.location.origin);
        }
        window.close();
      }).catch(() => {
        if (window.opener) {
          window.opener.postMessage({ provider: 'linkedin', error: 'LinkedIn login failed' }, window.location.origin);
        }
        window.close();
      });
    }
  }, [])

  const handleLinkedinLogin = () => {
    // Save state in sessionStorage for CSRF protection
    sessionStorage.setItem('linkedin_oauth_state', STATE);
    const scope = 'email profile openid';
    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&state=${STATE}&scope=${encodeURIComponent(scope)}`;
    const width = 600, height = 600;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);
    window.open(authUrl, 'LinkedIn Login', `width=${width},height=${height},top=${top},left=${left}`);

  };
  // console.log(isLoading,loginMethod,"loginMethod")

  return (
    <Secondary
      onClick={handleLinkedinLogin}
      sx={{ border: `1px solid #E0E0E6`, borderRadius: '10px', height: '54px', marginBottom: { xs: "0rem", sm: 0 } }}
      fullWidth
    >
      {isLoading && loginMethod == "linkedin" ? <CircularProgress sx={{ color: "black", marginInline: 'auto' }} size={'1.4rem'} /> : <Grid
        display={"flex"}
        gap={1}
        flexDirection={"row"}
        alignItems={"center"}
      >
        <img src={linkedInIcon} width={24} height={24} />
        <Typography
          variant="caption"
          sx={{ textTransform: "none", fontWeight: 500, fontSize: '1rem', color: '#344054' }}
        >
          Continue with LinkedIn
        </Typography>
      </Grid>}
    </Secondary>
  );
};

export default LinkedinLogin;


///// Attempt 3
// LinkedInLogin.js
// import React from 'react';
// import { signInWithLinkedIn } from '../../firebase/linkedinAuth';

// const LinkedinLogin = () => {
//   const handleLinkedInLogin = async () => {
//     try {
//       const user = await signInWithLinkedIn();
//       console.log('Logged in user:', user);
//     } catch (error) {
//       console.error('LinkedIn login failed:', error);
//     }
//   };

//   return (
//     <div>
//       <button onClick={handleLinkedInLogin}>
//         Login with LinkedIn
//       </button>
//     </div>
//   );
// };

// export default LinkedinLogin;

